import { createContext, useContext } from "react";

/**
 * Registers the element the mobile drawer measures to size its collapsed peek — the
 * sliver of drawer left on screen once it has been dragged all the way down.
 *
 * Pass the callback as a `ref` on the header's title row. The drawer observes the
 * registered element, so a title that wraps to two lines or a subtitle that arrives with
 * fetched data re-sizes the peek instead of being clipped by it.
 *
 * `SlottedWindow` and `Window`'s built-in chrome register their own title rows, so
 * anything rendered through either gets a correctly sized peek with no extra wiring.
 *
 * Content that owns its entire header must register when that header lives inside the
 * panel's scroller — `chromeVariant="headless"` puts it there — because the drawer's
 * fallback infers the header from where the content's scroll region begins, and a header
 * inside that region leaves nothing above it to measure; unregistered, such a panel gets a
 * fixed sliver. For the usual `Panel` / `Panel.Body` shape (`Window`'s `header` escape
 * hatch, or content rendered outside `Window`) inference is exact and registering is
 * optional: it pins the peek to the title row specifically, excluding any non-scrolling
 * rows beneath it (tabs, search, filters) that inference necessarily includes.
 *
 * The registered row's `flex-shrink` is pinned to 0 for as long as it is registered, so it
 * cannot be squeezed by the collapsing drawer and its height can be read as-is.
 *
 * Outside a drawer (desktop, docked, side panel) the callback is a stable no-op, so
 * header components can attach it unconditionally.
 */
export type RegisterDrawerPeekTarget = (node: HTMLElement | null) => void;

// Module-level so the default is referentially stable: an inline `() => {}` would be a
// new ref callback on every render, making React detach and reattach the node each time.
const noRegistration: RegisterDrawerPeekTarget = () => {};

export const DrawerPeekTargetContext = createContext<RegisterDrawerPeekTarget>(noRegistration);

export function useDrawerPeekTarget(): RegisterDrawerPeekTarget {
    return useContext(DrawerPeekTargetContext);
}
